import React, { useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Redirect, Switch, Route } from 'react-router-dom';
import ScrollToTop from './shared/components/ScrollTop';
import { Page } from './shared/components/Page/Page';
import LabResultDetails from './features/lab-results/routes/lab-result-details';
import LabeResultsList from './features/lab-results/routes/lab-results-list';
import Login from './features/auth/routes/login';
import { PrivateRoute } from './features/auth/components/PrivateRoute';
import { LoggedOutRoute } from './features/auth/components/LoggedOutRoute';
import { loadUser, selectLoggedIn } from './features/auth/state';
import { useDispatch, useSelector } from 'react-redux';
import { VersionNumber } from './shared/components/VersionNumber';
import LabRequestsList from './features/audit-trail/routes/lab-requests-list';
import LabRequestDetails from './features/audit-trail/routes/lab-request-details';
import { ROLES } from './shared/roles.enum';
import NotFound from './shared/components/NotFound';
import UserTrackerList from './features/user-tracker/routes/user-tracker-list';
import UserDetails from './features/user-tracker/routes/user-details';
import { LastLocationProvider } from 'react-router-last-location';
import UserSubmittedLabResultsList from './features/user-submitted-lab-results/routes/user-submitted-lab-results-list/UserSubmittedLabResultsList';
import UserSubmittedLabResultDetails from './features/user-submitted-lab-results/routes/user-submitted-lab-results-details/UserSubmittedLabResultDetails';

const privateRoutes = [
  { path: '/lab-results', component: LabeResultsList, roles: [ROLES.PHYSICIAN_OVERSIGHT] },
  { path: '/lab-results/:id', component: LabResultDetails, roles: [ROLES.PHYSICIAN_OVERSIGHT] },
  { path: '/audit-trail', component: LabRequestsList, roles: [ROLES.AUDIT_TRAIL] },
  { path: '/lab-requests/:id', component: LabRequestDetails, roles: [ROLES.AUDIT_TRAIL] },
  { path: '/user-tracker', component: UserTrackerList, roles: [ROLES.USER_TRACKER] },
  { path: '/users/:id', component: UserDetails, roles: [ROLES.USER_TRACKER] },
  {
    path: '/user-submitted-lab-results',
    component: UserSubmittedLabResultsList,
    roles: [ROLES.SUPPORT],
  },
  {
    path: '/user-submitted-lab-results/:userId/:resultId',
    component: UserSubmittedLabResultDetails,
    roles: [ROLES.SUPPORT],
  },
];

function App() {
  const dispatch = useDispatch();
  const stableDispatch = useCallback(dispatch, []);
  const auth = useSelector(selectLoggedIn);
  const roles = auth?.attributes.find(({ Name }: any) => Name === 'custom:roles');
  const parsedUserRoles = roles?.Value.split(',') || [];

  useEffect(() => {
    stableDispatch(loadUser());
  }, [stableDispatch]);

  if (auth === undefined) {
    return null;
  }

  return (
    <Router>
      <LastLocationProvider>
        <ScrollToTop />
        <Switch>
          <LoggedOutRoute
            exact
            path="/login"
            component={() => (
              <Page>
                <Login />
              </Page>
            )}
          />
          <PrivateRoute
            exact
            path="/"
            component={() => {
              const defaultRoute = privateRoutes.find(route => {
                return !!route.roles.find(r => parsedUserRoles.includes(r));
              });
              return <Redirect to={defaultRoute?.path || '/login'} />;
            }}
          />
          {privateRoutes
            .filter(route => !!route.roles.find(r => parsedUserRoles.includes(r)))
            .map(route => (
              <PrivateRoute
                exact
                key={route.path}
                path={route.path}
                component={() => (
                  <Page>
                    <route.component />
                  </Page>
                )}
              />
            ))}
          <Route
            component={() => (
              <Page>
                <NotFound />
              </Page>
            )}
          />
        </Switch>
        {auth === null && parsedUserRoles.length === 0 ? <Redirect to="/login" /> : null}
        <VersionNumber />
      </LastLocationProvider>
    </Router>
  );
}

export default App;
